import { useEffect, useMemo, useRef, useState } from 'react';
import type {
  FedExPickupPrefill,
  PickupAddress,
  PickupCarrierType,
} from '@/apis/fedexPickup';
import { useDebouncedValue } from '@/hooks/useDebouncedValue';
import { useFedExPickupAvailability } from '@/hooks/useFedExPickup';
import {
  clampDate,
  getDefaultPickupDate,
  getPickupDateBounds,
  isAddressComplete,
  nextBusinessDay,
  parseOriginToday,
} from './fedexPickupUtils';

const DEBOUNCE_MS = 400;

interface UsePickupScheduleParams {
  shipmentId: string;
  prefill: FedExPickupPrefill;
  carrierType: PickupCarrierType;
  accountNumber: string;
  address: PickupAddress;
}

/** Pickup date and ready/close times, kept in sync with FedEx's availability response. */
export const usePickupSchedule = ({
  shipmentId,
  prefill,
  carrierType,
  accountNumber,
  address,
}: UsePickupScheduleParams) => {
  const [pickupDate, setPickupDate] = useState(() =>
    getDefaultPickupDate(prefill.carrierType, prefill.originToday)
  );
  const [adjustedTo, setAdjustedTo] = useState<string | null>(null);
  const dateTouched = useRef(false);
  const [readyTime, setReadyTime] = useState('');
  const [closeTime, setCloseTime] = useState('');

  const bounds = getPickupDateBounds(carrierType, prefill.originToday);

  const currentRequest = useMemo(() => {
    return isAddressComplete(address) && pickupDate
      ? {
          shipmentId,
          accountNumber,
          carrierType,
          dispatchDate: pickupDate,
          address,
        }
      : null;
  }, [shipmentId, accountNumber, carrierType, pickupDate, address]);
  const { debounced: debouncedRequest, settled } = useDebouncedValue(
    currentRequest,
    DEBOUNCE_MS
  );
  const availability = useFedExPickupAvailability(debouncedRequest);
  const option = availability.data?.options.find(
    (o) => o.carrierType === carrierType
  );

  useEffect(() => {
    if (!option) return;
    const readyOptions = option.readyTimeOptions ?? [];
    const latestOptions = option.latestTimeOptions ?? [];
    setReadyTime((cur) =>
      readyOptions.includes(cur)
        ? cur
        : (option.defaultReadyTime ?? readyOptions[0] ?? '')
    );
    setCloseTime((cur) =>
      latestOptions.includes(cur)
        ? cur
        : (option.defaultLatestTime ??
          latestOptions[latestOptions.length - 1] ??
          '')
    );
  }, [option]);

  // Adopt FedEx's resolved working day (e.g. Saturday -> Monday).
  useEffect(() => {
    if (!option || !settled || !option.pickupDate) return;
    if (
      !option.available &&
      !dateTouched.current &&
      carrierType === 'EXPRESS' &&
      pickupDate === bounds.min
    ) {
      setPickupDate(
        nextBusinessDay(parseOriginToday(bounds.min)).format('YYYY-MM-DD')
      );
      return;
    }
    if (
      option.pickupDate > pickupDate &&
      option.pickupDate >= bounds.min &&
      option.pickupDate <= bounds.max
    ) {
      setAdjustedTo(option.pickupDate);
      setPickupDate(option.pickupDate);
    }
  }, [option, settled, pickupDate, bounds.min, bounds.max, carrierType]);

  const onCarrierChange = (next: PickupCarrierType) => {
    setAdjustedTo(null);
    setPickupDate((cur) =>
      dateTouched.current
        ? clampDate(cur, getPickupDateBounds(next, prefill.originToday))
        : getDefaultPickupDate(next, prefill.originToday)
    );
  };

  const handleDateChange = (value: string) => {
    if (!value) return;
    dateTouched.current = true;
    setAdjustedTo(null);
    setPickupDate(clampDate(value, bounds));
  };

  const availabilityReady =
    settled &&
    !availability.isFetching &&
    !!option?.available &&
    option.pickupDate === pickupDate;
  const notAvailable =
    settled &&
    !availability.isFetching &&
    !!availability.data &&
    !option?.available;
  const dateAdjustedAway =
    settled &&
    !!option?.pickupDate &&
    option.pickupDate !== pickupDate &&
    adjustedTo === null;

  return {
    availability,
    option,
    bounds,
    pickupDate,
    adjustedTo,
    readyTime,
    setReadyTime,
    closeTime,
    setCloseTime,
    handleDateChange,
    onCarrierChange,
    availabilityReady,
    notAvailable,
    dateAdjustedAway,
  };
};
